import { computed, Injectable, Signal, signal } from '@angular/core';

export interface FilterStore<T = Record<string, unknown>> {
  filters: T;
  lastUpdated: number;
}

@Injectable({
  providedIn: 'root'
})
export class FilterStorageService {
  private readonly STORAGE_KEY = 'umss_filter_storage';

  // Use a signal holding a Map to make it reactive.
  // The map stores the composite key (path + optional tab) string to FilterStore mapping.
  private state = signal<Map<string, FilterStore<unknown>>>(new Map());

  /**
   * Clean filters that are older than 3 days.
   */
  private readonly cleanIfOlderThanInDays = 3;

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Loads the filter data from sessionStorage to persist state across reloads.
   */
  private loadFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const map = new Map<string, FilterStore<unknown>>(
          Object.entries(parsed)
        );
        this.state.set(map);
      }
    } catch (e) {
      console.error('Failed to parse filters from storage', e);
    }
  }

  /**
   * Persists the current map state to sessionStorage.
   */
  private saveToStorage(currentMap: Map<string, FilterStore<unknown>>): void {
    try {
      const obj = Object.fromEntries(currentMap);
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      console.error('Failed to save filters to storage', e);
    }
  }

  /**
   * Generates a composite key based on the current window location path
   * and an optional tab identifier for pages that have multiple tabs
   * under the same route.
   */
  private getStorageKey(tabId?: string): string {
    const path = window.location.pathname;
    return tabId ? `${path}#${tabId}` : path;
  }

  /**
   * Creates or updates the filters for the current path (and optional tab).
   * @param filters The filter object to be stored
   * @param tabId Optional identifier for when multiple tabs exist in the same route
   */
  setFilters<T>(filters: T, tabId?: string): void {
    const key = this.getStorageKey(tabId);
    const currentMap = new Map(this.state());

    currentMap.set(key, {
      filters: filters as unknown,
      lastUpdated: Date.now()
    });

    this.state.set(currentMap);
    this.saveToStorage(currentMap);
  }

  /**
   * Partially updates existing filters instead of overwriting all of them.
   * Useful when updating a single filter form control.
   */
  patchFilters<T>(partialFilters: Partial<T>, tabId?: string): void {
    const currentStore = this.getFilterStore<T>(tabId);
    const existingFilters = currentStore?.filters || ({} as T);

    this.setFilters<T>({ ...existingFilters, ...partialFilters }, tabId);
  }

  /**
   * Retrieves the current filters synchronously.
   */
  getFilters<T>(tabId?: string): T | null {
    const store = this.getFilterStore<T>(tabId);
    return store ? store.filters : null;
  }

  /**
   * Retrieves the whole FilterStore object (containing filters + lastUpdated).
   */
  getFilterStore<T>(tabId?: string): FilterStore<T> | null {
    const key = this.getStorageKey(tabId);
    const filterStore = this.state().get(key);

    if (!filterStore) return null;
    const now = Date.now();
    const diffInDays = (now - filterStore.lastUpdated) / (1000 * 60 * 60 * 24);
    if (diffInDays <= this.cleanIfOlderThanInDays) {
      return filterStore as FilterStore<T>;
    }

    this.deleteFilters(tabId);
    return null;
  }

  /**
   * Retrieves the filters reactively as an Angular Signal.
   * Components can listen to this to react to any filter updates.
   */
  selectFilters<T>(tabId?: string): Signal<T | null> {
    const key = this.getStorageKey(tabId);
    return computed(() => {
      const filterStore = this.state().get(key);
      return filterStore ? (filterStore.filters as T) : null;
    });
  }

  /**
   * Deletes the filter store for the current path (and optional tab).
   */
  deleteFilters(tabId?: string): void {
    const key = this.getStorageKey(tabId);
    const currentMap = new Map(this.state());
    if (currentMap.has(key)) {
      currentMap.delete(key);
      this.state.set(currentMap);
      this.saveToStorage(currentMap);
    }
  }

  /**
   * Clears all filter stores across all paths.
   */
  clearAll(): void {
    this.state.set(new Map());
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Partially updates filters for an explicit path instead of the current window path.
   * Use this when one page needs to update the stored filters of another page.
   */
  patchFiltersForPath<T>(path: string, partialFilters: Partial<T>): void {
    const key = path;
    const currentStore = this.state().get(key);
    const existingFilters = (currentStore?.filters || {}) as T;
    const currentMap = new Map(this.state());
    currentMap.set(key, {
      filters: { ...existingFilters, ...partialFilters } as unknown,
      lastUpdated: Date.now()
    });
    this.state.set(currentMap);
    this.saveToStorage(currentMap);
  }
}
